import { useCallback } from "react";
import { z } from "zod";
import { getProductAttribute } from "@made-people/grease-product";
import { getAttributeValue, getProductSku, uniqueByKey } from "../helpers";
import type { NavigateFunction } from "react-router";
import type { GtmDataLayerEvent } from "@made-people/grease-gtm";
import type {
  Product,
  ProductBase,
  ProductPurchaseInfo,
} from "@made-people/grease-product";
import type { StylePickerItem } from "../settings";
import type { StylePickerStyle } from "./types";

export const useStylePickerTracking = (params: {
  href: (to: string) => string;
  navigate: NavigateFunction;
  pushCustomEvent: (data: GtmDataLayerEvent) => void;
  productBase: ProductBase;
  purchaseInfo: ProductPurchaseInfo;
  stylePickerItems: StylePickerItem[];
}) => {
  const normalizeString = useCallback((value: unknown) => {
    if (typeof value !== "string") {
      return "";
    }
    return value.toLowerCase();
  }, []);

  const getStringAttribute = useCallback(
    (base: ProductBase, attributeKey: string) => {
      return getProductAttribute(base.attributes, attributeKey, z.string())
        ?.value;
    },
    [],
  );

  const getStringArrayAttribute = useCallback(
    (base: ProductBase, attributeKey: string) => {
      return getProductAttribute(
        base.attributes,
        attributeKey,
        z.array(z.string()),
      )?.value;
    },
    [],
  );

  const getProductFlags = useCallback(
    (base: ProductBase) => {
      const closure = normalizeString(getStringAttribute(base, "closure"));
      const wiring = normalizeString(getStringAttribute(base, "wiring"));
      const sideWingValue = normalizeString(
        getStringAttribute(base, "sidewing"),
      );
      const technicalFeatures =
        getStringArrayAttribute(base, "technical_features")?.map((value) =>
          value.toLowerCase(),
        ) ?? [];
      const solutions =
        getStringArrayAttribute(base, "solutions")?.map((value) =>
          value.toLowerCase(),
        ) ?? [];

      const hasBackClosure =
        closure.includes("rygg") || closure.includes("back");
      const isWired = wiring.includes("bygel") && !wiring.includes("utan");
      const hasSideWing =
        sideWingValue.includes("side") ||
        technicalFeatures.some((value) => value.includes("sidvinge")) ||
        solutions.some((value) => value.includes("sidvinge"));

      return { hasBackClosure, isWired, hasSideWing };
    },
    [getStringArrayAttribute, getStringAttribute, normalizeString],
  );

  const getBooleanAttribute = useCallback(
    (base: ProductBase, attributeKey: string) => {
      const parsed = getProductAttribute(
        base.attributes,
        attributeKey,
        z.boolean(),
      );
      if (parsed) {
        return parsed.value;
      }
      return undefined;
    },
    [],
  );

  const isActiveStyle = useCallback(
    (style: StylePickerStyle) => {
      const feature = style.item.trackingKey;
      const productFlags = getProductFlags(params.productBase);
      const styleFlags = getProductFlags(style.product.base);

      if (
        !styleFlags.hasBackClosure &&
        !productFlags.hasBackClosure &&
        feature === "hasBackClosure"
      ) {
        return true;
      }
      if (
        styleFlags.hasBackClosure &&
        productFlags.hasBackClosure &&
        feature === "isWired"
      ) {
        return styleFlags.isWired === productFlags.isWired;
      }
      if (
        styleFlags.hasBackClosure &&
        productFlags.hasBackClosure &&
        feature === "hasSideWing"
      ) {
        return styleFlags.hasSideWing === productFlags.hasSideWing;
      }
      return false;
    },
    [getProductFlags, params.productBase],
  );

  const getProductTrackingData = useCallback(
    (
      product: ProductBase,
      sku: string,
      url: string,
      key: string,
      toggleValues?: string[],
    ) => {
      const directBoolean = getBooleanAttribute(product, key);
      const derivedFlags = getProductFlags(product);
      const resolvedBoolean =
        typeof directBoolean === "boolean"
          ? directBoolean
          : key === "hasBackClosure"
            ? derivedFlags.hasBackClosure
            : key === "isWired"
              ? derivedFlags.isWired
              : key === "hasSideWing"
                ? derivedFlags.hasSideWing
                : false;
      const toggleValue = resolvedBoolean
        ? toggleValues?.[0]
        : toggleValues?.[1];

      return {
        name: product.content.name,
        sku,
        url,
        toggleValue,
      };
    },
    [getBooleanAttribute, getProductFlags],
  );

  const trackData = useCallback(
    (sourceBase: ProductBase, target: Product) => {
      const items = uniqueByKey(params.stylePickerItems, "trackingKey");
      const data = [];

      for (const feature of items) {
        if (!feature.trackingKey) {
          continue;
        }
        if (
          getAttributeValue(sourceBase, feature.trackingKey) ===
          getAttributeValue(target.base, feature.trackingKey)
        ) {
          continue;
        }
        const toggleValues = feature.toggleValues
          ?.split(",")
          .map((value) => value.trim());
        data.push({
          source_product: getProductTrackingData(
            sourceBase,
            params.purchaseInfo.sku,
            params.href(sourceBase.slug),
            feature.trackingKey,
            toggleValues,
          ),
          target_product: getProductTrackingData(
            target.base,
            getProductSku(target),
            params.href(target.base.slug),
            feature.trackingKey,
            toggleValues,
          ),
        });
      }

      return data;
    },
    [
      getProductTrackingData,
      params.href,
      params.purchaseInfo.sku,
      params.stylePickerItems,
    ],
  );

  const handleSubmit = useCallback(
    (style: StylePickerStyle) => {
      const targetUrl = params.href(style.product.base.slug);
      const sourceUrl = params.href(params.productBase.slug);

      if (sourceUrl === targetUrl) {
        return;
      }

      const features = trackData(params.productBase, style.product);
      for (const feature of features) {
        params.pushCustomEvent({
          event: "comfort_suite_click",
          comfortSuiteClickEvent: {
            products: feature,
            context: "StylePicker",
          },
        });
      }

      params.navigate(targetUrl);
    },
    [
      params.href,
      params.navigate,
      params.productBase,
      params.pushCustomEvent,
      trackData,
    ],
  );

  return { handleSubmit, isActiveStyle };
};
